/**
 * Logins panel (dashboard launch screen).
 *
 * One row per login provider status, with the single applicable action per
 * type/mode/status: direct-OIDC popup connect, brokered re-authenticate
 * (deep-links to the main platform login), or static secret entry. Inline
 * role=status/alert feedback only — no toasts.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@features/auth';
import { ROUTES } from '@utils/constants';
import { useLogins, useStartLogin, useSetStaticSecret, useDisconnectLogin } from '../hooks/useLogins';
import type { LoginStatus } from '../types';
import { saveLoginFlow, LOGIN_COMPLETE_MESSAGE } from '../loginFlowStorage';
import { LoginRow } from './LoginRow';
import { StaticSecretDialog } from './StaticSecretDialog';

interface LoginsPanelProps {
  /** Whether the current user has an active (building/starting/running) workspace. */
  hasActiveWorkspace: boolean;
}

export function LoginsPanel({ hasActiveWorkspace }: LoginsPanelProps): ReactElement {
  const { data: logins, isLoading, error } = useLogins();
  const startLogin = useStartLogin();
  const setStaticSecret = useSetStaticSecret();
  const disconnectLogin = useDisconnectLogin();
  const { login: reauthenticate } = useAuth();
  const queryClient = useQueryClient();

  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [secretDialogLogin, setSecretDialogLogin] = useState<LoginStatus | null>(null);

  const deliveredMessage = hasActiveWorkspace
    ? 'Credentials delivered — takes effect in your workspace within a minute.'
    : null;

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: unknown } | null;
      if (data?.type !== LOGIN_COMPLETE_MESSAGE) return;
      void queryClient.invalidateQueries({ queryKey: ['logins'] });
      setActionError(null);
      setMessage(deliveredMessage ?? 'Login connected.');
    };
    window.addEventListener('message', handleMessage);
    return () => { window.removeEventListener('message', handleMessage); };
  }, [queryClient, deliveredMessage]);

  const handleDirectLogin = async (login: LoginStatus): Promise<void> => {
    setActionError(null);
    setMessage(null);
    try {
      const redirectUri = `${window.location.origin}${ROUTES.LOGIN_CALLBACK}`;
      const resp = await startLogin.mutateAsync({ name: login.name, redirectUri });
      saveLoginFlow({
        state: resp.state,
        code_verifier: resp.code_verifier,
        provider: login.name,
        redirect_uri: redirectUri,
      });
      window.open(resp.url, 'sanctum-login', 'width=600,height=700');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Failed to start login for ${login.display_name}`);
    }
  };

  const handleSetSecret = (secret: string): void => {
    if (!secretDialogLogin) return;
    setActionError(null);
    setStaticSecret.mutate(
      { name: secretDialogLogin.name, secret },
      {
        onSuccess: () => {
          setSecretDialogLogin(null);
          setMessage(deliveredMessage ?? 'Secret saved.');
        },
        onError: (err) => {
          setActionError(err instanceof Error ? err.message : 'Failed to save secret');
        },
      },
    );
  };

  const handleDisconnect = (login: LoginStatus): void => {
    setActionError(null);
    setMessage(null);
    disconnectLogin.mutate(login.name, {
      onSuccess: () => { setMessage(deliveredMessage ?? 'Disconnected.'); },
      onError: (err) => {
        setActionError(err instanceof Error ? err.message : `Failed to disconnect ${login.display_name}`);
      },
    });
  };

  if (isLoading) {
    return (
      <section className="logins-panel">
        <h2 className="logins-panel__title">Logins</h2>
        <div className="loading-message">Loading logins...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="logins-panel">
        <h2 className="logins-panel__title">Logins</h2>
        <div className="error-message" role="alert">{error.message}</div>
      </section>
    );
  }

  if (!logins || logins.length === 0) {
    return (
      <section className="logins-panel">
        <h2 className="logins-panel__title">Logins</h2>
        <p className="logins-panel__empty">No login providers are configured.</p>
      </section>
    );
  }

  return (
    <section className="logins-panel">
      <h2 className="logins-panel__title">Logins</h2>

      {message && <p className="logins-panel__message" role="status">{message}</p>}
      {actionError && <p className="logins-panel__error" role="alert">{actionError}</p>}

      <div className="logins-panel__list">
        {logins.map((login) => (
          <LoginRow
            key={login.name}
            login={login}
            onDirectLogin={(l) => { void handleDirectLogin(l); }}
            onReauthenticate={() => { void reauthenticate(); }}
            onSetSecret={setSecretDialogLogin}
            onDisconnect={handleDisconnect}
            isStartingLogin={startLogin.isPending}
            isDisconnecting={disconnectLogin.isPending}
          />
        ))}
      </div>

      <StaticSecretDialog
        open={secretDialogLogin !== null}
        login={secretDialogLogin}
        onClose={() => { setSecretDialogLogin(null); }}
        onSubmit={handleSetSecret}
        isSubmitting={setStaticSecret.isPending}
      />
    </section>
  );
}
